import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ChevronRight, ChevronLeft, CheckCircle, AlertCircle } from 'lucide-react';
import {
  Venue,
  Service,
  AddOn,
  BookingState,
  Appointment,
  Company,
  CheckoutPricing,
  BookingAvailabilitySlot
} from '@/types';
import { api } from '@/services/api';
import { tokenizeCardDetails } from '@/services/opayo';
import { filterPastAvailabilitySlots } from '@/lib/scheduling';
import { formatDateOnly } from '@/lib/dates';
import { normalizeDobToIso } from '@/lib/formatters';
import { buildMapById } from '@/lib/collections';
import { validateUkPhone, validateEmail, validateDob } from '@/lib/validators';
import { buildBrowserInfo } from '@/lib/opayo';
import { getTierPrice } from '@/lib/pricing';
import { shouldShowInlineFormError } from '@/lib/bookingFlowErrors';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import { ApiError } from '@/lib/apiError';
import { getDefaultCountryCode } from '@/constants';
import { ThreeDSData, PaymentResultModal } from './bookingFlowTypes';
import { getBookingPolicyLink, getDistanceFromLatLonInMiles } from './bookingFlowUtils';
import { VenuePicker } from './VenuePicker';
import { ServicePicker } from './ServicePicker';
import { DateTimePicker } from './DateTimePicker';
import { PatientForm } from './PatientForm';
import { PaymentPanel } from './PaymentPanel';
import { OrderSummary } from './OrderSummary';

interface BookingFlowProps {
  venues: Venue[];
  services: Service[];
  addOns: AddOn[];
  servicesById?: Map<string, Service>;
  venuesById?: Map<string, Venue>;
  addOnsById?: Map<string, AddOn>;
  appointments: Appointment[];
  onComplete: (booking: Appointment) => Promise<void> | void;
  authenticatedCompany?: Company;
  termsAndConditions: string;
  onBack?: () => void;
}

export const BookingFlow: React.FC<BookingFlowProps> = ({
  venues,
  services,
  addOns,
  servicesById: servicesByIdFromProps,
  venuesById: venuesByIdFromProps,
  addOnsById: addOnsByIdFromProps,
  appointments: _appointments,
  onComplete,
  authenticatedCompany,
  termsAndConditions,
  onBack
}) => {
  const [threeDSData, setThreeDSData] = useState<ThreeDSData | null>(null);
  const [state, setState] = useState<BookingState>({
    step: 0,
    mode: authenticatedCompany ? 'company' : 'individual',
    venueId: null,
    serviceIds: [],
    addOnIds: [],
    date: null,
    time: null,
    customerDetails: { name: '', email: '', phone: '', dob: '', jobTitle: '', poNumber: '' }
  });
  const currentCompany = authenticatedCompany || null;
  const servicesById = useMemo(
    () => servicesByIdFromProps ?? buildMapById(services),
    [servicesByIdFromProps, services]
  );
  const addOnsById = useMemo(
    () => addOnsByIdFromProps ?? buildMapById(addOns),
    [addOnsByIdFromProps, addOns]
  );
  const venuesById = useMemo(
    () => venuesByIdFromProps ?? buildMapById(venues),
    [venuesByIdFromProps, venues]
  );
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expiry: '',
    cvc: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    address1: '',
    city: '',
    postalCode: '',
    country: getDefaultCountryCode()
  });
  const [userPostcode, setUserPostcode] = useState('');
  const [distances, setDistances] = useState<Record<string, number>>({});
  const [isSearchingPostcode, setIsSearchingPostcode] = useState(false);
  const [postcodeError, setPostcodeError] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [formError, setFormError] = useState('');
  const [dobTouched, setDobTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [paymentResultModal, setPaymentResultModal] = useState<PaymentResultModal | null>(null);
  const [completedAppointment, setCompletedAppointment] = useState<Appointment | null>(null);
  const [checkoutPricing, setCheckoutPricing] = useState<CheckoutPricing | null>(null);
  const [pendingPaymentId, setPendingPaymentId] = useState<string | undefined>(undefined);
  const [pendingPaymentLinkToken, setPendingPaymentLinkToken] = useState<string | undefined>(
    undefined
  );
  const [availabilitySlots, setAvailabilitySlots] = useState<BookingAvailabilitySlot[]>([]);
  const [hasAvailabilityData, setHasAvailabilityData] = useState(false);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isFindingNextAvailable, setIsFindingNextAvailable] = useState(false);
  const [nextAvailableMessage, setNextAvailableMessage] = useState<string | null>(null);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const nameValue = state.customerDetails.name.trim();
  const isNameValid = nameValue.split(/\s+/).filter(Boolean).length >= 2;
  const showNameError = nameTouched && !isNameValid;
  const dobValue = state.customerDetails.dob.trim();
  const isDobValid = validateDob(dobValue).valid;
  const showDobError = dobTouched && !isDobValid;
  const emailValue = state.customerDetails.email.trim();
  const isEmailValid = validateEmail(emailValue).valid;
  const showEmailError = emailTouched && !isEmailValid;
  const isCompanyBooking = state.mode === 'company' || Boolean(authenticatedCompany);
  const isPhoneRequired = !isCompanyBooking;
  const phoneValue = state.customerDetails.phone.trim();
  const hasPhoneValue = phoneValue.length > 0;
  const isPhoneValid = isPhoneRequired
    ? hasPhoneValue && validateUkPhone(phoneValue).valid
    : !hasPhoneValue || validateUkPhone(phoneValue).valid;
  const showPhoneError = phoneTouched && !isPhoneValid;
  const bookingPolicyLink = getBookingPolicyLink(termsAndConditions);

  const getPrice = (item: Service | AddOn) => getTierPrice(item, { company: currentCompany });

  const selectedServices = useMemo(
    () =>
      state.serviceIds
        .map((id) => servicesById.get(id))
        .filter((service): service is Service => Boolean(service)),
    [state.serviceIds, servicesById]
  );
  const selectedService = selectedServices[0];
  // The £0 Health Surveillance shell only makes sense with at least one test.
  const requiresAddOnSelection = selectedServices.some(
    (service) =>
      Number(getPrice(service)) <= 0 && service.name.toLowerCase().includes('health surveillance')
  );
  const availableAddOns = useMemo(
    () =>
      addOns.filter(
        (addon) =>
          state.serviceIds.length === 0 ||
          addon.compatibleServiceIds.some((id) => state.serviceIds.includes(id))
      ),
    [addOns, state.serviceIds]
  );
  const compatibleAddOnIds = useMemo(
    () => new Set(availableAddOns.map((a) => a.id)),
    [availableAddOns]
  );
  const hasCompatibleAddOn = state.addOnIds.some((id) => compatibleAddOnIds.has(id));
  const isAddOnSelectionMissing = requiresAddOnSelection && !hasCompatibleAddOn;

  const getTotalDuration = useCallback(() => {
    let duration = 0;
    selectedServices.forEach((service) => {
      duration += service.durationMinutes || 0;
    });
    state.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) duration += addon.durationMinutes || 0;
    });
    return duration;
  }, [selectedServices, state.addOnIds, addOnsById]);

  const getPricingForCheckout = useCallback((): CheckoutPricing => {
    if (checkoutPricing) return checkoutPricing;
    let subtotalAmount = 0;
    selectedServices.forEach((service) => {
      subtotalAmount += Number(getPrice(service));
    });
    state.addOnIds.forEach((id) => {
      const addon = addOnsById.get(id);
      if (addon) subtotalAmount += Number(getPrice(addon));
    });
    return {
      currency: 'GBP',
      subtotalAmount,
      vatRatePercent: 0,
      vatAmount: 0,
      totalAmount: subtotalAmount
    };
  }, [checkoutPricing, selectedServices, state.addOnIds, addOnsById, currentCompany]);

  const finalizeBooking = useCallback(
    async (overrides?: { paymentId?: string; paymentLinkToken?: string }) => {
      const pricing = getPricingForCheckout();
      const phone = state.customerDetails.phone.trim();
      const phoneResult = phone ? validateUkPhone(phone) : null;
      const hasQuestionnaire =
        selectedServices.some((service) => service.questionnaireConfig?.templateId) ||
        state.addOnIds.some((id) => addOnsById.get(id)?.questionnaireConfig?.templateId);
      const resolvedPaymentMethod =
        state.mode === 'company' || authenticatedCompany
          ? currentCompany?.paymentPreference || 'invoice'
          : 'card';
      const resolvedPaymentId = overrides?.paymentId ?? pendingPaymentId;
      const resolvedPaymentLinkToken = overrides?.paymentLinkToken ?? pendingPaymentLinkToken;
      const newAppointment: Appointment = {
        id: '',
        venueId: state.venueId!,
        serviceId: state.serviceIds[0],
        serviceIds: state.serviceIds,
        addOnIds: state.addOnIds,
        // Local calendar day, not UTC (see availability call) — avoids the
        // off-by-one that stored bookings on the day before the one selected.
        date: formatDateOnly(state.date!),
        time: state.time!,
        customerName: state.customerDetails.name,
        customerEmail: state.customerDetails.email,
        customerPhone: phoneResult?.valid ? phoneResult.normalized : '',
        customerDob: normalizeDobToIso(state.customerDetails.dob),
        customerJobTitle: state.customerDetails.jobTitle,
        companyId: currentCompany?.id,
        poNumber: isCompanyBooking ? state.customerDetails.poNumber : undefined,
        paymentMethod: resolvedPaymentMethod,
        paymentId: resolvedPaymentMethod === 'card' ? resolvedPaymentId : undefined,
        paymentLinkToken: resolvedPaymentMethod === 'card' ? resolvedPaymentLinkToken : undefined,
        subtotalAmount: pricing.subtotalAmount,
        vatRatePercent: pricing.vatRatePercent,
        vatAmount: pricing.vatAmount,
        totalAmount: pricing.totalAmount,
        currency: pricing.currency,
        status: 'booked',
        createdAt: new Date().toISOString(),
        questionnaireStatus: hasQuestionnaire ? 'pending' : 'not_required'
      };
      setIsProcessingPayment(false);
      setPendingPaymentId(undefined);
      setPendingPaymentLinkToken(undefined);
      setCompletedAppointment(newAppointment);
      setPaymentResultModal({
        type: 'success',
        title: 'Booking Confirmed',
        message:
          'Your appointment has been booked and a confirmation email will be sent to you shortly.',
        shouldFinalize: false
      });
    },
    [
      servicesById,
      addOnsById,
      state,
      currentCompany,
      authenticatedCompany,
      pendingPaymentId,
      pendingPaymentLinkToken,
      getPricingForCheckout,
      isCompanyBooking,
      selectedServices
    ]
  );

  useEffect(() => {
    if (authenticatedCompany) setState((prev) => ({ ...prev, mode: 'company' }));
  }, [authenticatedCompany]);

  useEffect(() => {
    setNextAvailableMessage(null);
  }, [state.venueId, state.serviceIds, state.addOnIds]);

  useEffect(() => {
    if (!currentCompany?.allowedVenueIds?.length || !state.venueId) return;
    if (currentCompany.allowedVenueIds.includes(state.venueId)) return;
    setState((prev) => ({ ...prev, venueId: null, date: null, time: null }));
  }, [currentCompany, state.venueId]);

  useEffect(() => {
    if (state.serviceIds.length === 0) {
      setCheckoutPricing(null);
      return;
    }
    let isCurrent = true;
    api
      .getCheckoutQuote({
        serviceId: state.serviceIds[0],
        serviceIds: state.serviceIds,
        addOnIds: state.addOnIds,
        companyId: currentCompany?.id,
        currency: 'GBP'
      })
      .then((r) => {
        if (isCurrent) setCheckoutPricing(r.pricing);
      })
      .catch(() => {
        if (isCurrent) setCheckoutPricing(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [state.serviceIds, state.addOnIds, currentCompany?.id]);

  useEffect(() => {
    if (!state.date || !state.venueId || state.serviceIds.length === 0) {
      setAvailabilitySlots([]);
      setHasAvailabilityData(false);
      setIsLoadingAvailability(false);
      return;
    }
    let isCurrent = true;
    setIsLoadingAvailability(true);
    setHasAvailabilityData(false);
    api
      .getBookingAvailability({
        venueId: state.venueId,
        // Local calendar day, not UTC: state.date.toISOString() rolls the day
        // back one for UK/negative-offset users (24th selected -> 23rd sent).
        date: formatDateOnly(state.date),
        serviceId: state.serviceIds[0],
        serviceIds: state.serviceIds,
        addOnIds: state.addOnIds
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(state.date, result?.slots || []);
        setAvailabilitySlots(slots);
        setHasAvailabilityData(true);
        setState((prev) => {
          if (!prev.time) return prev;
          const ok = slots.some((s) => s.time === prev.time && s.available);
          return ok ? prev : { ...prev, time: null };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setAvailabilitySlots([]);
        setHasAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingAvailability(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [state.date, state.venueId, state.serviceIds, state.addOnIds]);

  // A 3DS challenge has two completion signals: the postMessage from the notify page
  // (fast path) and the payment-status poll (reliable path — survives a lost message,
  // a blocked iframe, or a closed challenge window). Whichever lands first wins; the
  // ref makes resolution idempotent so the other signal becomes a no-op.
  const threeDSResolvedRef = useRef(false);

  const resolveThreeDSSuccess = useCallback(
    (paymentId?: string, paymentLinkToken?: string) => {
      if (threeDSResolvedRef.current) return;
      threeDSResolvedRef.current = true;
      setThreeDSData(null);
      if (paymentId) setPendingPaymentId(paymentId);
      if (paymentLinkToken) setPendingPaymentLinkToken(paymentLinkToken);
      // Finalize immediately with fresh values — don't wait for the user to click
      // "Continue" on the modal, which would be lost if they close the tab.
      void finalizeBooking({ paymentId, paymentLinkToken });
    },
    [finalizeBooking]
  );

  const resolveThreeDSFailure = useCallback((message: string) => {
    if (threeDSResolvedRef.current) return;
    threeDSResolvedRef.current = true;
    setThreeDSData(null);
    setFormError(message);
    setIsProcessingPayment(false);
    setPaymentResultModal({
      type: 'error',
      title: 'Payment Failed',
      message,
      shouldFinalize: false
    });
  }, []);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!event?.data || event.data.type !== 'opayo:3ds') return;
      if (!event.data.success) {
        resolveThreeDSFailure(event.data.statusDetail || '3DS verification failed.');
        return;
      }
      resolveThreeDSSuccess(
        event.data.paymentId || undefined,
        event.data.paymentLinkToken || undefined
      );
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [resolveThreeDSSuccess, resolveThreeDSFailure]);

  useEffect(() => {
    if (!threeDSData || !pendingPaymentId) return;
    const paymentId = pendingPaymentId;
    const paymentLinkToken = pendingPaymentLinkToken;
    const email = state.customerDetails.email.trim();
    let cancelled = false;
    const interval = window.setInterval(async () => {
      try {
        const result = await api.getOpayoPaymentStatus({ paymentId, paymentLinkToken, email });
        if (cancelled) return;
        if (result.status === 'captured') {
          resolveThreeDSSuccess(paymentId, paymentLinkToken);
        } else if (result.status === 'failed') {
          resolveThreeDSFailure(result.errorMessage || '3DS verification failed.');
        }
      } catch {
        // Transient poll errors are fine — the postMessage path may still land,
        // and the next tick retries.
      }
    }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    threeDSData,
    pendingPaymentId,
    pendingPaymentLinkToken,
    state.customerDetails.email,
    resolveThreeDSSuccess,
    resolveThreeDSFailure
  ]);

  const handleThreeDSCancel = useCallback(async () => {
    const paymentId = pendingPaymentId;
    const paymentLinkToken = pendingPaymentLinkToken;
    const email = state.customerDetails.email.trim();
    if (paymentId) {
      try {
        const result = await api.getOpayoPaymentStatus({ paymentId, paymentLinkToken, email });
        if (result.status === 'captured') {
          resolveThreeDSSuccess(paymentId, paymentLinkToken);
          return;
        }
        if (result.status === 'failed') {
          resolveThreeDSFailure(result.errorMessage || '3DS verification failed.');
          return;
        }
      } catch {
        // Status unavailable — fall through to the unconfirmed message.
      }
    }
    // Outcome still unknown: stop the challenge UI, but deliberately leave the
    // outcome unresolved — if the bank's confirmation lands moments later, the
    // message listener still completes the booking.
    setThreeDSData(null);
    setIsProcessingPayment(false);
    setPaymentResultModal({
      type: 'error',
      title: 'Payment Not Confirmed',
      message:
        'We could not confirm your payment. If you completed the bank verification, you will receive a confirmation email shortly — please check your email before trying to pay again.',
      shouldFinalize: false
    });
  }, [
    pendingPaymentId,
    pendingPaymentLinkToken,
    state.customerDetails.email,
    resolveThreeDSSuccess,
    resolveThreeDSFailure
  ]);

  useEffect(() => {
    if (!threeDSData) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = threeDSData.acsUrl;
    form.target = 'opayo-3ds-iframe';
    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };
    addField('creq', threeDSData.cReq);
    addField('TermUrl', threeDSData.termUrl);
    if (threeDSData.threeDSSessionData)
      addField('threeDSSessionData', threeDSData.threeDSSessionData);
    document.body.appendChild(form);
    form.submit();
    return () => {
      document.body.removeChild(form);
    };
  }, [threeDSData]);

  const findNextAvailableSlot = useCallback(async () => {
    if (isFindingNextAvailable) return;
    if (!state.venueId || state.serviceIds.length === 0) {
      setNextAvailableMessage('Select a location and service to find availability.');
      return;
    }
    setIsFindingNextAvailable(true);
    setNextAvailableMessage(null);
    try {
      const maxDays = 60;
      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      const startDate = state.date ? new Date(state.date) : new Date(minDate);
      startDate.setHours(0, 0, 0, 0);
      if (startDate < minDate) startDate.setTime(minDate.getTime());
      const startOffset = state.date ? 1 : 0;
      for (let i = startOffset; i < maxDays; i += 1) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        const result = await api.getBookingAvailability({
          venueId: state.venueId,
          date,
          serviceId: state.serviceIds[0],
          serviceIds: state.serviceIds,
          addOnIds: state.addOnIds
        });
        const slots = filterPastAvailabilitySlots(date, result?.slots || []);
        const available = slots.find((s) => s.available);
        if (available) {
          setState((prev) => ({ ...prev, date, time: available.time }));
          setVisibleMonth(new Date(date.getFullYear(), date.getMonth(), 1));
          setNextAvailableMessage(
            `Next available: ${date.toLocaleDateString('en-GB', { dateStyle: 'full' })} at ${available.time}.`
          );
          return;
        }
      }
      setNextAvailableMessage(
        `No availability in the next ${maxDays} days. Try another location or service.`
      );
    } catch {
      setNextAvailableMessage('Unable to load next available appointments right now.');
    } finally {
      setIsFindingNextAvailable(false);
    }
  }, [state.venueId, state.serviceIds, state.addOnIds, state.date, isFindingNextAvailable]);

  const handleScheduleContinue = () => {
    if (
      isAddOnSelectionMissing ||
      !state.venueId ||
      state.serviceIds.length === 0 ||
      !state.date ||
      !state.time
    )
      return;
    setState((prev) => ({
      ...prev,
      mode: authenticatedCompany ? 'company' : prev.mode,
      step: 2
    }));
  };

  const handleCalculateDistances = async () => {
    if (!userPostcode.trim()) return;
    setIsSearchingPostcode(true);
    setPostcodeError('');
    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${userPostcode.trim()}`);
      const data = await response.json();
      if (data.status === 200 && data.result) {
        const { latitude, longitude } = data.result;
        const newDistances: Record<string, number> = {};
        venues.forEach((venue) => {
          if (venue.coordinates) {
            newDistances[venue.id] = parseFloat(
              getDistanceFromLatLonInMiles(
                latitude,
                longitude,
                venue.coordinates.lat,
                venue.coordinates.lng
              ).toFixed(1)
            );
          }
        });
        setDistances(newDistances);
      } else {
        setPostcodeError('Invalid UK Postcode');
      }
    } catch {
      setPostcodeError('Unable to calculate distances');
    } finally {
      setIsSearchingPostcode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessingPayment) return;
    setFormError('');
    const phone = phoneValue;
    const phoneResult = phone ? validateUkPhone(phone) : null;
    if (isAddOnSelectionMissing) {
      setFormError('Please select at least one add-on for Health Surveillance bookings.');
      return;
    }
    if (!isNameValid) {
      setNameTouched(true);
      setFormError('Please enter both a first name and a surname.');
      return;
    }
    if (!isDobValid) {
      setDobTouched(true);
      setFormError(
        `Please enter a valid date of birth in DD-MM-YYYY format (year 1900-${new Date().getFullYear()}).`
      );
      return;
    }
    if (!isEmailValid) {
      setEmailTouched(true);
      setFormError('Please enter a valid email address.');
      return;
    }
    if (!isPhoneValid) {
      setPhoneTouched(true);
      return;
    }
    if (isCompanyBooking) {
      const normalizedEmail = emailValue.toLowerCase();
      const reserved = new Set<string>();
      const ce = String(currentCompany?.email || '')
        .trim()
        .toLowerCase();
      if (ce) reserved.add(ce);
      (currentCompany?.teamMembers || []).forEach((m) => {
        const me = String(m?.email || '')
          .trim()
          .toLowerCase();
        if (me) reserved.add(me);
      });
      if (normalizedEmail && reserved.has(normalizedEmail)) {
        setFormError(
          "This email is linked to your company portal account and cannot be used as a candidate email. Please enter the candidate's own email address."
        );
        return;
      }
    }
    const requiresCardPayment = !isCompanyBooking || currentCompany?.paymentPreference === 'card';
    const cleanCardNumber = cardDetails.number.replace(/\D/g, '');
    const cleanCvc = cardDetails.cvc.replace(/\D/g, '');
    if (isCompanyBooking && currentCompany?.requirePo && !state.customerDetails.poNumber?.trim()) {
      setFormError('Your company requires a Purchase Order (PO) number.');
      return;
    }
    if (requiresCardPayment && !cardDetails.cardholderName.trim()) {
      setFormError('Please enter the cardholder name.');
      return;
    }
    if (
      requiresCardPayment &&
      (cleanCardNumber.length < 12 ||
        cleanCardNumber.length > 19 ||
        cardDetails.expiry.length < 5 ||
        cleanCvc.length < 3 ||
        cleanCvc.length > 4)
    ) {
      setFormError('Please enter valid credit card details.');
      return;
    }
    if (
      requiresCardPayment &&
      (!billingAddress.address1.trim() ||
        !billingAddress.city.trim() ||
        !billingAddress.postalCode.trim() ||
        !billingAddress.country.trim())
    ) {
      setFormError('Please enter your billing address.');
      return;
    }
    if (requiresCardPayment && !/^[A-Za-z]{2}$/.test(billingAddress.country.trim())) {
      setFormError('Please select a valid billing country.');
      return;
    }
    if (!agreedToTerms) return;
    setIsProcessingPayment(true);
    if (requiresCardPayment) {
      setPendingPaymentId(undefined);
      setPendingPaymentLinkToken(undefined);
      threeDSResolvedRef.current = false;
      if (state.serviceIds.length === 0) {
        setFormError('Please select a service.');
        setIsProcessingPayment(false);
        return;
      }
      let chargeAttempted = false;
      try {
        const quote = await api.getCheckoutQuote({
          serviceId: state.serviceIds[0],
          serviceIds: state.serviceIds,
          addOnIds: state.addOnIds,
          companyId: currentCompany?.id,
          currency: 'GBP'
        });
        setCheckoutPricing(quote.pricing);
        if (quote.pricing.totalAmount > 0) {
          const expiryDigits = cardDetails.expiry.replace(/\D/g, '').slice(0, 4);
          const nameParts = state.customerDetails.name.trim().split(/\s+/).filter(Boolean);
          const session = await api.createOpayoSessionKey();
          const cardIdentifier = await tokenizeCardDetails({
            session,
            cardDetails: {
              number: cleanCardNumber,
              expiry: expiryDigits,
              cvc: cardDetails.cvc,
              cardholderName:
                cardDetails.cardholderName.trim() || state.customerDetails.name.trim() || 'Customer'
            }
          });
          chargeAttempted = true;
          const paymentResult = await api.processOpayoPayment({
            amount: quote.pricing.totalAmount,
            expectedTotal: quote.pricing.totalAmount,
            currency: 'GBP',
            description: `K2 Booking - ${
              selectedServices.map((service) => service.name).join(' + ') || 'Appointment'
            }`,
            merchantSessionKey: session.merchantSessionKey,
            cardIdentifier,
            billingAddress: {
              address1: billingAddress.address1.trim(),
              city: billingAddress.city.trim(),
              postalCode: billingAddress.postalCode.trim(),
              country: billingAddress.country.trim().toUpperCase()
            },
            customer: {
              firstName: nameParts[0] || 'Customer',
              lastName: nameParts.slice(1).join(' ') || 'Customer',
              email: state.customerDetails.email.trim(),
              phone: phoneResult?.valid ? phoneResult.normalized : ''
            },
            browser: buildBrowserInfo(),
            serviceId: state.serviceIds[0],
            serviceIds: state.serviceIds,
            addOnIds: state.addOnIds,
            companyId: currentCompany?.id,
            venueId: state.venueId ?? undefined,
            // Local calendar day, not UTC — .toISOString().slice(0,10) rolled
            // the day back one for UK users, so the captured payment (and any
            // recovered booking) landed on the day before the one selected.
            date: state.date ? formatDateOnly(state.date) : undefined,
            time: state.time ?? undefined,
            customerName: state.customerDetails.name.trim() || undefined,
            customerDob: normalizeDobToIso(state.customerDetails.dob) || undefined,
            // These must travel with the charge: since the server creates the
            // booking from the captured payment's metadata, the follow-up
            // POST /bookings is an idempotent replay that writes nothing.
            customerJobTitle: (state.customerDetails.jobTitle || '').trim() || undefined,
            poNumber: isCompanyBooking
              ? (state.customerDetails.poNumber || '').trim() || undefined
              : undefined
          });
          if (paymentResult?.paymentId) setPendingPaymentId(paymentResult.paymentId);
          if (paymentResult?.paymentLinkToken)
            setPendingPaymentLinkToken(paymentResult.paymentLinkToken);
          if (
            paymentResult?.requires3DS &&
            paymentResult.acsUrl &&
            paymentResult.cReq &&
            paymentResult.termUrl
          ) {
            setThreeDSData({
              acsUrl: paymentResult.acsUrl,
              cReq: paymentResult.cReq,
              termUrl: paymentResult.termUrl,
              threeDSSessionData: paymentResult.threeDSSessionData
            });
            return;
          }
          await finalizeBooking();
          return;
        }
      } catch (err: unknown) {
        // A timed-out charge is NOT a failed charge: the money may have been taken
        // and the booking created server-side. Say so instead of inviting a retry.
        const isChargeTimeout = chargeAttempted && err instanceof ApiError && err.status === 408;
        const message = isChargeTimeout
          ? 'We could not confirm whether your payment completed. If it did, you will receive a confirmation email shortly — please check your email before trying again.'
          : normalizeErrorMessage(err, 'Payment failed. Please try again.');
        setFormError(message);
        setPaymentResultModal({
          type: 'error',
          title: isChargeTimeout ? 'Payment Not Confirmed' : 'Payment Failed',
          message,
          shouldFinalize: false
        });
        setIsProcessingPayment(false);
        return;
      }
    }
    await finalizeBooking();
  };

  const closePaymentResultModal = () => {
    const wasSuccess = paymentResultModal?.type === 'success';
    const appointment = completedAppointment;
    setPaymentResultModal(null);
    if (wasSuccess && appointment) {
      // Never dead-end silently: a rejected finalize used to drop the user back on
      // the filled payment form with no message, which pushed customers into paying
      // a second time. Surface the server's reason instead.
      void Promise.resolve(onComplete(appointment)).catch((err: unknown) => {
        setPaymentResultModal({
          type: 'error',
          title: 'Booking Needs Attention',
          message: normalizeErrorMessage(
            err,
            'We could not finalise your booking automatically. Please check your email for a confirmation before booking again.'
          ),
          shouldFinalize: false
        });
      });
    }
  };

  const showInlineFormError = shouldShowInlineFormError(formError, paymentResultModal);

  const paymentResultModalElement = paymentResultModal ? (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center border border-slate-200">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${paymentResultModal.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}
        >
          {paymentResultModal.type === 'success' ? (
            <CheckCircle className="w-8 h-8" />
          ) : (
            <AlertCircle className="w-8 h-8" />
          )}
        </div>
        <h4 className="text-lg font-bold text-slate-900 mb-2">
          {paymentResultModal.title ||
            (paymentResultModal.type === 'success' ? 'Payment Successful' : 'Payment Failed')}
        </h4>
        <p className="text-slate-600 text-sm mb-6">{paymentResultModal.message}</p>
        <button
          onClick={closePaymentResultModal}
          className={`w-full py-2.5 rounded-xl text-sm font-bold text-white ${paymentResultModal.type === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
        >
          {paymentResultModal.type === 'success' ? 'Continue' : 'Close'}
        </button>
      </div>
    </div>
  ) : null;

  const availableVenues =
    state.mode === 'company' && currentCompany?.allowedVenueIds?.length
      ? venues.filter((v) => currentCompany.allowedVenueIds!.includes(v.id))
      : venues;

  if (state.step === 0) {
    const canContinue = Boolean(
      state.venueId &&
      state.serviceIds.length > 0 &&
      state.date &&
      state.time &&
      !isAddOnSelectionMissing
    );
    return (
      <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-8 animate-fade-in">
        {paymentResultModalElement}
        {onBack && (
          <button
            onClick={onBack}
            className="text-slate-500 hover:text-slate-800 mb-6 flex items-center gap-1 text-sm"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        )}
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
            1
          </span>
          Select a Location
        </h2>
        <VenuePicker
          venues={availableVenues}
          selectedVenueId={state.venueId}
          distances={distances}
          userPostcode={userPostcode}
          isSearchingPostcode={isSearchingPostcode}
          postcodeError={postcodeError}
          onPostcodeChange={setUserPostcode}
          onFindNearest={handleCalculateDistances}
          onVenueSelect={(venueId) =>
            setState((prev) =>
              prev.venueId === venueId ? prev : { ...prev, venueId, date: null, time: null }
            )
          }
        />
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
              2
            </span>
            Choose Service
          </h2>
          <ServicePicker
            services={services}
            availableAddOns={availableAddOns}
            selectedServiceIds={state.serviceIds}
            selectedAddOnIds={state.addOnIds}
            requiresAddOnSelection={requiresAddOnSelection}
            isAddOnSelectionMissing={isAddOnSelectionMissing}
            getPrice={getPrice}
            onServiceToggle={(serviceId, checked) =>
              setState((p) => {
                const serviceIds = checked
                  ? [...p.serviceIds, serviceId]
                  : p.serviceIds.filter((id) => id !== serviceId);
                // Drop add-ons no longer compatible with any remaining service.
                const addOnIds = p.addOnIds.filter((addOnId) => {
                  const addon = addOnsById.get(addOnId);
                  return addon?.compatibleServiceIds.some((id) => serviceIds.includes(id));
                });
                return { ...p, serviceIds, addOnIds, time: null };
              })
            }
            onAddOnToggle={(addOnId, checked) =>
              setState((p) => ({
                ...p,
                addOnIds: checked
                  ? [...p.addOnIds, addOnId]
                  : p.addOnIds.filter((id) => id !== addOnId)
              }))
            }
          />
        </div>
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
              3
            </span>
            Select Date &amp; Time
          </h2>
          <DateTimePicker
            selectedVenueId={state.venueId}
            selectedVenue={state.venueId ? venuesById.get(state.venueId) : undefined}
            selectedServiceId={state.serviceIds[0] ?? null}
            selectedDate={state.date}
            selectedTime={state.time}
            visibleMonth={visibleMonth}
            availabilitySlots={availabilitySlots}
            hasAvailabilityData={hasAvailabilityData}
            isLoadingAvailability={isLoadingAvailability}
            isFindingNextAvailable={isFindingNextAvailable}
            nextAvailableMessage={nextAvailableMessage}
            requiredDuration={getTotalDuration()}
            onDateSelect={(d) => {
              setNextAvailableMessage(null);
              setState((prev) => ({ ...prev, date: d, time: null }));
            }}
            onTimeSelect={(time) => {
              setNextAvailableMessage(null);
              setState((prev) => ({ ...prev, time }));
            }}
            onMonthChange={(offset) => {
              const nextMonth = new Date(
                visibleMonth.getFullYear(),
                visibleMonth.getMonth() + offset,
                1
              );
              const minMonthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
              if (offset < 0 && nextMonth.getTime() < minMonthStart.getTime()) return;
              setVisibleMonth(nextMonth);
              setState((prev) => {
                if (!prev.date) return prev;
                const inNext =
                  prev.date.getFullYear() === nextMonth.getFullYear() &&
                  prev.date.getMonth() === nextMonth.getMonth();
                return inNext ? prev : { ...prev, date: null, time: null };
              });
            }}
            onFindNextAvailable={findNextAvailableSlot}
          />
        </div>
        <div className="mt-10 flex justify-end">
          <button
            onClick={handleScheduleContinue}
            disabled={!canContinue}
            className="bg-slate-900 text-white px-10 py-3 rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            Continue <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (state.step === 2) {
    const pricing = getPricingForCheckout();
    const requiresCardPayment = !isCompanyBooking || currentCompany?.paymentPreference === 'card';
    return (
      <div className="w-full px-4 sm:px-8 lg:px-12 xl:px-16 py-8 animate-fade-in">
        {paymentResultModalElement}
        <button
          onClick={() => setState((p) => ({ ...p, step: 0 }))}
          className="text-slate-500 hover:text-slate-800 mb-4 flex items-center gap-1 text-sm"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <span className="bg-teal-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-black">
            4
          </span>
          Confirm Booking
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <OrderSummary
            selectedServices={selectedServices}
            selectedAddOnIds={state.addOnIds}
            addOnsById={addOnsById}
            venue={state.venueId ? venuesById.get(state.venueId) : undefined}
            pricing={pricing}
            getPrice={getPrice}
          />
          <div className="space-y-4">
            <PatientForm
              customerDetails={state.customerDetails}
              isCompanyBooking={isCompanyBooking}
              isPhoneRequired={isPhoneRequired}
              currentCompany={currentCompany}
              showNameError={showNameError}
              showDobError={showDobError}
              showEmailError={showEmailError}
              showPhoneError={showPhoneError}
              hasPhoneValue={hasPhoneValue}
              onCustomerDetailsChange={(details) =>
                setState((p) => ({ ...p, customerDetails: details }))
              }
              onNameBlur={() => setNameTouched(true)}
              onDobBlur={() => setDobTouched(true)}
              onEmailBlur={() => setEmailTouched(true)}
              onPhoneBlur={(normalized) => {
                setPhoneTouched(true);
                if (normalized) {
                  setState((p) => ({
                    ...p,
                    customerDetails: { ...p.customerDetails, phone: normalized }
                  }));
                }
              }}
            />
            <PaymentPanel
              requiresCardPayment={requiresCardPayment}
              cardDetails={cardDetails}
              billingAddress={billingAddress}
              pricing={pricing}
              agreedToTerms={agreedToTerms}
              isProcessingPayment={isProcessingPayment}
              showInlineFormError={showInlineFormError}
              formError={formError}
              threeDSData={threeDSData}
              bookingPolicyLink={bookingPolicyLink}
              onCardDetailsChange={setCardDetails}
              onBillingAddressChange={setBillingAddress}
              onAgreedToTermsChange={setAgreedToTerms}
              onSubmit={handleSubmit}
              onThreeDSCancel={handleThreeDSCancel}
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};
